// layerInfo.js
// - Sort mode: 'configuration', 'alpha', or 'map' (topmost first)
// - Alphabetical sort: case-insensitive, numeric-aware, unnamed layers pushed to the end
// - Keeps checkboxes in sync when visibility changes anywhere
// - Base Data expanded by default

import Group from 'ol/layer/Group.js';
import { getMap } from './mapInstance.js';
import { registerDock, setDockVisible } from './panelDockManager.js';
import { placeFloatingDialog } from './dialogs.js';
import { getLayerVisibilityRange, isViewInLayerVisibilityRange, resolutionToZoom, updateLayerVisibilityByZoom } from './mapUtils.js';
import { createQgisLayerControls } from './qgisLayerInfoDialog.js';
import { showPoiSetContextMenu } from './poiSetContextMenu.js';


const SORT_MODE = 'configuration';
const layerToCheckbox = new WeakMap(); // map Layer -> its checkbox DOM node
const layerToLabel = new WeakMap(); // map Layer -> its label DOM node
const layerToRow = new WeakMap(); // map Layer -> its row DOM node
const featureToLocationCheckbox = new WeakMap();
const collapsedPoiSets = new Set();
const collapsedPoiCategories = new Set();
const poiCategorySearchQueries = new Map();
// Show search only when a category has more locations than this threshold.
// Set to null to enable search in every category.
const POI_CATEGORY_SEARCH_THRESHOLD = 5;
const hiddenPoiLocationStyle = () => null;

function getPoiLocationFeatures(layer) {
  return layer?.getSource?.()?.getFeatures?.() || [];
}

function hasPoiLocations(layer) {
  return !!layer.get('poiSetId') || layer.get('searchPoiCategory') === true;
}

function isPoiLocationVisible(feature) {
  return feature?.get?.('_poiLocationVisible') !== false;
}

function setPoiLocationVisible(feature, visible) {
  feature?.set?.('_poiLocationVisible', !!visible, true);
  feature?.setStyle?.(visible ? undefined : hiddenPoiLocationStyle);
  const checkbox = featureToLocationCheckbox.get(feature);
  if (checkbox) checkbox.checked = !!visible;
}

function filterPoiCategoryLocations(markerList, categoryKey) {
  const query = (poiCategorySearchQueries.get(categoryKey) || '').trim().toLowerCase();
  let matchCount = 0;
  markerList.querySelectorAll('.poi-location-row').forEach(location => {
    const name = location.querySelector('.poi-location-name')?.textContent || '';
    const matches = !query || name.toLowerCase().includes(query);
    location.hidden = !matches;
    if (matches) matchCount++;
  });
  const status = markerList.querySelector('.poi-category-search-status');
  if (status) {
    status.textContent = query ? (matchCount ? `${matchCount} location(s) found` : 'No results found ...!') : '';
    status.hidden = !query;
  }
}

function collapseAllPoiTreeRoots(container = document.getElementById('own-layers')) {
  if (!container) return;
  container.querySelectorAll('.poi-set-row').forEach(row => {
    const key = String(row.dataset.poiSetId || '');
    if (key) collapsedPoiSets.add(key);
    const toggle = row.querySelector('.poi-set-toggle');
    if (toggle) { toggle.textContent = '▶'; toggle.setAttribute('aria-expanded', 'false'); }
  });
  container.querySelectorAll('.poi-category-row').forEach(row => {
    const categoryKey = row.dataset.poiCategoryKey;
    if (categoryKey) collapsedPoiCategories.add(categoryKey);
    row.hidden = !!row.dataset.poiSetId;
    const toggle = row.querySelector('.poi-category-toggle');
    if (toggle) { toggle.textContent = '▶'; toggle.setAttribute('aria-expanded', 'false'); }
    const locations = row.querySelector('.poi-location-list');
    if (locations) locations.hidden = true;
  });
}

function isOverlayGroup(group) {
  return group === window.overlays;
}

function isBaseGroup(group) {
  return group === window.base_map;
}


function getGroupMapping() {
  return [
    { group: window.base_map, ulId: 'base-layers', groupCbId: 'base-group-cb' },
    { group: window.overlays, ulId: 'overlay-layers', groupCbId: 'overlay-group-cb' },
    { group: window.own_map, ulId: 'own-layers', groupCbId: 'own-group-cb' },
  ];
}

function getSortedLayers(group) {

  //const arr = group.getLayers().getArray().slice();

    // Filter out nested groups — only show real layers
  const arr = group.getLayers()
    .getArray()
    .filter(l => !(l instanceof Group)) // exclude Group layers
    .filter(l => l.get('title') && l.get('title').trim() !== ''); // exclude unnamed

  if (SORT_MODE === 'map') {
    // Map draw order: last item is topmost -> reverse so topmost shows first
    return arr.slice().reverse();
  }

  if (SORT_MODE === 'configuration') {
    if (!isBaseGroup(group)) {
      return arr.slice();
    }

    return arr.slice().sort((a, b) => {
      const rawOrderA = a.get('displayOrder');
      const rawOrderB = b.get('displayOrder');
      const orderA = Number(rawOrderA);
      const orderB = Number(rawOrderB);
      const hasOrderA = rawOrderA !== null && rawOrderA !== undefined && Number.isFinite(orderA);
      const hasOrderB = rawOrderB !== null && rawOrderB !== undefined && Number.isFinite(orderB);

      if (hasOrderA && hasOrderB && orderA !== orderB) return orderA - orderB;
      if (hasOrderA !== hasOrderB) return hasOrderA ? -1 : 1;

      const rangeA = getLayerVisibilityRange(a);
      const rangeB = getLayerVisibilityRange(b);
      const startZoom = range => {
        if (range.mode === 'zoom') return Number(range.minZoom);
        if (range.mode === 'resolution' && Number.isFinite(range.maxResolution)) {
          return resolutionToZoom(range.maxResolution);
        }
        return -Infinity;
      };
      const endZoom = range => {
        if (range.mode === 'zoom') return Number(range.maxZoom);
        if (range.mode === 'resolution' && Number.isFinite(range.minResolution) && range.minResolution > 0) {
          return resolutionToZoom(range.minResolution);
        }
        return Infinity;
      };
      const startA = startZoom(rangeA);
      const startB = startZoom(rangeB);
      if (startA !== startB) return startB - startA;

      const endA = endZoom(rangeA);
      const endB = endZoom(rangeB);
      if (endA !== endB) return endB - endA;

      return String(a.get('title') || '').localeCompare(
        String(b.get('title') || ''),
        undefined,
        { sensitivity: 'base', numeric: true }
      );
    });
  }

  // Alphabetical: push unnamed layers to the end, compare titles locale-aware
  return arr.slice().sort((a, b) => {
    const ta = (a.get('title') || '').trim();
    const tb = (b.get('title') || '').trim();

    if (!ta && tb) return 1;   // unnamed goes last
    if (!tb && ta) return -1;  // unnamed goes last
    if (!ta && !tb) return 0;

    return ta.localeCompare(tb, undefined, { sensitivity: 'base', numeric: true });
  });
}

function getLayerFeatureCount(layer) {
  return layer?.getSource?.()?.getFeatures?.()?.length || 0;
}

function updateGroupCheckbox(groupCb, group) {

  if (!groupCb || !group) return;

  // Ignore rights-disabled layers
  const allowedLayers = group.getLayers()
    .getArray()
    .filter(l => !l.get('disabledByRights'));

  // No allowed layers → disable group checkbox
  if (allowedLayers.length === 0) {

    groupCb.checked = false;
    groupCb.indeterminate = false;
    groupCb.disabled = true;

    // Optional visual feedback
    groupCb.style.opacity = '0.5';
    groupCb.style.cursor = 'not-allowed';

    return;
  }

  // Enable checkbox again if at least one layer allowed
  groupCb.disabled = false;
  groupCb.style.opacity = '';
  groupCb.style.cursor = '';

  const visibles = allowedLayers.flatMap(layer => {
    const features = hasPoiLocations(layer) ? getPoiLocationFeatures(layer) : [];
    return features.length
      ? features.map(feature => !!layer.getVisible() && isPoiLocationVisible(feature))
      : [!!layer.getVisible()];
  });

  const allOn = visibles.every(Boolean);
  const allOff = visibles.every(v => !v);

  groupCb.checked = allOn;
  groupCb.indeterminate = !allOn && !allOff;
}

function findGroupForLayer(layer) {
  const mapping = getGroupMapping();
  for (const m of mapping) {
    if (!m.group) continue;
    if (m.group.getLayers().getArray().includes(layer)) return m;
  }
  return null;
}

function syncLayerCheckbox(layer) {
  const cb = layerToCheckbox.get(layer);
  const locationFeatures = hasPoiLocations(layer) ? getPoiLocationFeatures(layer) : [];
  const anyLocationVisible = locationFeatures.some(isPoiLocationVisible);
  const allLocationsVisible = locationFeatures.length === 0 || locationFeatures.every(isPoiLocationVisible);
  if (cb) {
    cb.checked = !!layer.getVisible() && allLocationsVisible;
    cb.indeterminate = !!layer.getVisible() && anyLocationVisible && !allLocationsVisible;
  }
  const row = layerToRow.get(layer);
  if (row) row.classList.toggle('layer-is-hidden', !layer.getVisible());
  const poiSetId = layer.get('poiSetId');
  if (poiSetId) {
    document.querySelectorAll('#own-layers .poi-set-row').forEach(setRow => {
      if (setRow.dataset.poiSetId !== String(poiSetId)) return;
      const group = window.own_map;
      const setLayers = group?.getLayers?.().getArray().filter(candidate =>
        String(candidate.get('poiSetId')) === String(poiSetId)
      ) || [];
      const effectiveVisibility = setLayers.flatMap(candidate => {
        const features = getPoiLocationFeatures(candidate);
        return features.length ? features.map(feature => candidate.getVisible() && isPoiLocationVisible(feature)) : [candidate.getVisible()];
      });
      const anyVisible = effectiveVisibility.some(Boolean);
      const allVisible = effectiveVisibility.length > 0 && effectiveVisibility.every(Boolean);
      const setCheckbox = setRow.querySelector(':scope > input[type="checkbox"]');
      if (setCheckbox) {
        setCheckbox.checked = allVisible;
        setCheckbox.indeterminate = anyVisible && !allVisible;
      }
      setRow.classList.toggle('layer-is-hidden', !anyVisible);
    });
  }
  updateLayerLabelState(layer);

  // update the group-level checkbox for this layer's group
  const groupMeta = findGroupForLayer(layer);
  if (groupMeta) {
    const groupCb = document.getElementById(groupMeta.groupCbId);
    updateGroupCheckbox(groupCb, groupMeta.group);
  }
}

function isLayerVisibleAtCurrentZoom(layer) {
  const map = getMap();
  if (!map || !layer) return false;
  if (!layer.getVisible?.()) return false;

  const resolution = map.getView()?.getResolution();
  const zoom = map.getView()?.getZoom?.();
  if (!Number.isFinite(resolution)) return false;

  return !layer.get('disabledByRights') &&
    layer.get('backendVisible') !== false &&
    isViewInLayerVisibilityRange(layer, resolution, zoom);
}

function updateLayerLabelState(layer) {
  const label = layerToLabel.get(layer);
  const active = isLayerVisibleAtCurrentZoom(layer);
  const row = layerToRow.get(layer);
  const isBaseLayer = window.base_map?.getLayers?.().getArray().includes(layer);

  if (row) {
    row.classList.toggle(
      'layer-is-hidden',
      isBaseLayer ? !active : !layer.getVisible()
    );
  }

  if (label) {
    label.style.fontWeight = active ? '700' : '600';
    label.style.color = active ? 'var(--sevenn-primary)' : '';
  }
}

function getLayerTargetZoom(layer) {
  const configuredTarget = layer.get('layerInfoTargetZoom');
  if (Number.isFinite(configuredTarget)) return configuredTarget;
  const range = getLayerVisibilityRange(layer);

  if (range.mode === 'zoom' && Number.isFinite(range.minZoom)) {
    return Number.isFinite(range.maxZoom)
      ? range.minZoom + ((range.maxZoom - range.minZoom) / 2)
      : range.minZoom + 0.5;
  }

  if (!Number.isFinite(range.maxResolution) || range.maxResolution <= 0) {
    return null;
  }

  const startZoom = resolutionToZoom(range.maxResolution);
  if (Number.isFinite(range.minResolution) && range.minResolution > 0) {
    const endZoom = resolutionToZoom(range.minResolution);
    return startZoom + ((endZoom - startZoom) / 2);
  }
  return startZoom + 0.5;
}

function zoomToLayerMinimum(layer) {
  const map = getMap();
  const view = map?.getView?.();
  if (!view || !layer) return;

  if (layer.get('disabledByRights')) return;

  if (layer.get('backendVisible') !== false && !layer.getVisible?.()) {
    layer.setVisible(true);
    syncLayerCheckbox(layer);
  }

  // Vector/Own Data layers have their extent in the client-side source and
  // usually do not define a minimum zoom. Fit their actual content first.
  const sourceExtent = layer.getSource?.()?.getExtent?.();
  const hasFiniteExtent = Array.isArray(sourceExtent) &&
    sourceExtent.length === 4 &&
    sourceExtent.every(Number.isFinite) &&
    sourceExtent[0] <= sourceExtent[2] &&
    sourceExtent[1] <= sourceExtent[3];

  const isOwnDataLayer = window.own_map?.getLayers?.().getArray().includes(layer);
  if (isOwnDataLayer && hasFiniteExtent) {
    window.own_map?.setVisible?.(true);
    view.fit(sourceExtent, {
      padding: [80, 80, 80, 80],
      maxZoom: 16,
      duration: 350
    });
    return;
  }

  const targetZoom = getLayerTargetZoom(layer);
  if (!Number.isFinite(targetZoom)) return;

  const currentZoom = view.getZoom?.();
  if (Number.isFinite(currentZoom) && Math.abs(currentZoom - targetZoom) < 0.01) {
    refreshLayerAfterZoom(layer, map);
    return;
  }

  view.animate({
    zoom: targetZoom,
    duration: 350
  }, completed => {
    if (completed !== false) {
      refreshLayerAfterZoom(layer, map);
    }
  });
}

function refreshLayerAfterZoom(layer, map) {
  updateLayerVisibilityByZoom(window.base_map);

  const view = map.getView?.();
  const size = map.getSize?.();
  const extent = view && size ? view.calculateExtent(size) : null;
  const resolution = view?.getResolution?.();
  const projection = view?.getProjection?.();
  const source = layer.getSource?.();

  if (source?.refresh) {
    source.refresh();
  } else if (source?.clear) {
    source.clear(true);
  }

  if (source?.loadFeatures && extent && Number.isFinite(resolution) && projection) {
    source.loadFeatures(extent, resolution, projection);
  }

  layer.changed?.();
  map.render?.();
}

function refreshLayerLabelStates() {
  getGroupMapping().forEach(({ group }) => {
    group?.getLayers?.().forEach(layer => updateLayerLabelState(layer));
  });
}

function getSelectedAreaLayers() {
  const result = [];
  const visit = layers => layers?.forEach?.(layer => {
    if (layer instanceof Group) visit(layer.getLayers());
    else if (layer.get?.('selectedAreasKind')) result.push(layer);
  });
  visit(getMap()?.getLayers?.());
  return result;
}

function populateSelectedAreas() {
  const group = document.getElementById('selected-areas-group');
  const list = document.getElementById('selected-area-layers');
  const groupCheckbox = document.getElementById('selected-areas-group-cb');
  if (!group || !list || !groupCheckbox) return;
  const entries = [];
  getSelectedAreaLayers().forEach(layer => {
    if (layer.get('selectedAreasKind') === 'pc') {
      (layer.get('getSelectedAreaEntries')?.() || []).forEach(entry => entries.push({ ...entry, layer, setVisible: visible => layer.get('setSelectedAreaVisibility')?.(entry.key, visible), zoomTo: () => layer.get('zoomToSelectedArea')?.(entry.key) }));
      return;
    }
    const count = layer.getSource?.()?.getFeatures?.()?.length || 0;
    if (!count) return;
    entries.push({ key: 'address', label: layer.get('selectedAreaLabel') || 'Address Selection', color: layer.get('selectedAreaColor') || '#ffcc33', count, visible: layer.getVisible?.() !== false, layer, setVisible: visible => layer.setVisible(visible) });
  });
  const sortedBaseLayers = window.base_map ? getSortedLayers(window.base_map) : [];
  const selectedAreaScaleRank = entry => {
    const key = String(entry.key || '').trim().toUpperCase();
    if (key === 'ADDRESS') return -1;
    const baseLayerIndex = sortedBaseLayers.findIndex(layer => {
      const searchable = [layer.get?.('title'), layer.get?.('name'), layer.get?.('role'), layer.get?.('typeName')]
        .filter(Boolean).join(' ').toUpperCase();
      return new RegExp(`(^|[^A-Z0-9])${key}([^A-Z0-9]|$)`).test(searchable);
    });
    if (baseLayerIndex >= 0) return baseLayerIndex;
    const postcodeLevel = Number(key.match(/^PC(\d+)$/)?.[1]);
    return Number.isFinite(postcodeLevel) ? 1000 - postcodeLevel : Number.POSITIVE_INFINITY;
  };
  entries.sort((a, b) => selectedAreaScaleRank(a) - selectedAreaScaleRank(b));
  list.innerHTML = '';
  const hasSelectionsOnMap = entries.some(entry => Number(entry.count) > 0);
  group.hidden = !hasSelectionsOnMap;
  group.style.display = hasSelectionsOnMap ? '' : 'none';
  group.setAttribute('aria-hidden', String(!hasSelectionsOnMap));
  if (!entries.length) { groupCheckbox.checked = false; groupCheckbox.indeterminate = false; groupCheckbox.disabled = true; return; }
  groupCheckbox.disabled = false;
  entries.forEach(entry => {
    const item = document.createElement('li');
    item.className = 'layer-info-row selected-area-row';
    item.classList.toggle('layer-is-hidden', !entry.visible);
    const row = document.createElement('div');
    row.className = 'layer-info-row-main';
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox'; checkbox.checked = entry.visible; checkbox.setAttribute('aria-label', `Show ${entry.label}`);
    checkbox.addEventListener('change', () => entry.setVisible(checkbox.checked));
    const label = document.createElement('span'); label.className = 'layer-name'; label.textContent = `${entry.label} (${entry.count})`;
    if (entry.zoomTo) {
      label.classList.add('selected-area-zoom-link'); label.title = `Zoom to all ${entry.label} features`; label.tabIndex = 0;
      const zoomToSelection = event => { if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return; event.preventDefault(); entry.zoomTo(); };
      label.addEventListener('click', zoomToSelection); label.addEventListener('keydown', zoomToSelection);
    }
    const legend = document.createElement('div'); legend.className = 'layer-legend-details';
    const symbol = document.createElement('span'); symbol.className = `layer-legend-symbol${entry.key === 'address' ? ' is-point' : ''}`;
    symbol.style.setProperty('--legend-fill', `${entry.color}40`); symbol.style.setProperty('--legend-stroke', entry.color); symbol.title = `${entry.label} selection color`;
    legend.appendChild(symbol); row.append(checkbox, label, legend); item.appendChild(row); list.appendChild(item);
  });
  const visibleCount = entries.filter(entry => entry.visible).length;
  groupCheckbox.checked = visibleCount === entries.length;
  groupCheckbox.indeterminate = visibleCount > 0 && visibleCount < entries.length;
  groupCheckbox.onchange = () => { entries.forEach(entry => entry.setVisible(groupCheckbox.checked)); groupCheckbox.indeterminate = false; };
}

/**
 * Build the panel contents for each group.
 * This function only rebuilds the layer rows (UL contents) and hooks up
 * listeners. It avoids tearing down the group headers so collapse state is preserved.
 */
export function populateLayerPanel() {
  populateSelectedAreas();
  const mapping = getGroupMapping();

  mapping.forEach(({ group, ulId, groupCbId }) => {
    const ul = document.getElementById(ulId);
    const groupCb = document.getElementById(groupCbId);
    if (!ul || !group) return;
    const groupDiv = ul.closest('.layer-group');

    // clear child rows (headers / toggle buttons are outside the UL)
    ul.innerHTML = '';

    const sorted = getSortedLayers(group);
    const allowedLayers = sorted.filter(layer => !layer.get('disabledByRights'));

    if (group === window.own_map) {
      const poiTotal = allowedLayers
        .reduce((total, layer) => total + getLayerFeatureCount(layer), 0);
      const rootCount = document.getElementById('poi-root-count');
      if (rootCount) rootCount.textContent = String(poiTotal);
    }

    if (allowedLayers.length === 0) {
      if (groupDiv) groupDiv.style.display = 'none';
      updateGroupCheckbox(groupCb, group);
      return;
    }

    if (groupDiv) groupDiv.style.display = '';

    const renderedPoiSets = new Set();

    sorted.forEach(layer => {

      const poiSetId = layer.get('poiSetId');
      const poiSetName = layer.get('poiSetName');
      if (group === window.own_map && poiSetId && poiSetName && !renderedPoiSets.has(poiSetId)) {
        renderedPoiSets.add(poiSetId);

        const setLayers = sorted.filter(candidate => candidate.get('poiSetId') === poiSetId);
        const setFeatureCount = setLayers.reduce(
          (total, candidate) => total + getLayerFeatureCount(candidate),
          0
        );
        const setRow = document.createElement('li');
        setRow.className = 'layer-info-row poi-set-row';
        setRow.dataset.poiSetId = String(poiSetId);
        setRow.classList.toggle('layer-is-hidden', !setLayers.some(candidate => candidate.getVisible()));
        setRow.style.listStyle = 'none';
        setRow.style.margin = '6px 0 3px';

        const setToggle = document.createElement('button');
        setToggle.type = 'button';
        setToggle.className = 'poi-tree-toggle poi-set-toggle';
        setToggle.textContent = collapsedPoiSets.has(String(poiSetId)) ? '▶' : '▼';
        setToggle.setAttribute('aria-label', `Toggle ${poiSetName}`);
        setToggle.setAttribute('aria-expanded', String(!collapsedPoiSets.has(String(poiSetId))));
        setToggle.addEventListener('click', event => {
          event.preventDefault(); event.stopPropagation();
          const key = String(poiSetId);
          if (collapsedPoiSets.has(key)) collapsedPoiSets.delete(key); else collapsedPoiSets.add(key);
          const collapsed = collapsedPoiSets.has(key);
          setToggle.textContent = collapsed ? '▶' : '▼';
          setToggle.setAttribute('aria-expanded', String(!collapsed));
          ul.querySelectorAll('.poi-category-row').forEach(categoryRow => {
            if (categoryRow.dataset.poiSetId === key) categoryRow.hidden = collapsed;
          });
        });

        const setCheckbox = document.createElement('input');
        setCheckbox.type = 'checkbox';
        const setLocationVisibility = setLayers.flatMap(candidate => {
          const features = getPoiLocationFeatures(candidate);
          return features.length ? features.map(feature => candidate.getVisible() && isPoiLocationVisible(feature)) : [candidate.getVisible()];
        });
        setCheckbox.checked = setLocationVisibility.length > 0 && setLocationVisibility.every(Boolean);
        setCheckbox.indeterminate = !setCheckbox.checked && setLocationVisibility.some(Boolean);
        setCheckbox.addEventListener('change', () => {
          const checked = setCheckbox.checked;
          setLayers.forEach(candidate => {
            if (candidate.get('disabledByRights')) return;
            getPoiLocationFeatures(candidate).forEach(feature => setPoiLocationVisible(feature, checked));
            candidate.setVisible(checked);
            syncLayerCheckbox(candidate);
          });
          updateGroupCheckbox(groupCb, group);
        });

        const setLabel = document.createElement('strong');
        setLabel.className = 'poi-set-name';
        setLabel.textContent = ` ${poiSetName}`;

        const setCount = document.createElement('span');
        setCount.className = 'poi-node-count';
        setCount.textContent = String(setFeatureCount);

        const setLegendIcon = document.createElement('img');
        setLegendIcon.className = 'poi-set-legend-icon';
        setLegendIcon.src = setLayers[0]?.get('poiLegendIconSrc') || '';
        setLegendIcon.alt = '';
        setLegendIcon.title = `${poiSetName} icon`;

        const setActionsButton = document.createElement('button');
        setActionsButton.type = 'button';
        setActionsButton.className = 'poi-set-actions-button';
        setActionsButton.title = `${poiSetName} actions`;
        setActionsButton.setAttribute('aria-label', `${poiSetName} actions`);
        setActionsButton.setAttribute('aria-haspopup', 'menu');
        setActionsButton.innerHTML = '<i class="bi bi-three-dots-vertical" aria-hidden="true"></i>';
        setActionsButton.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();
          const rect = setActionsButton.getBoundingClientRect();
          showPoiSetContextMenu({
            clientX: rect.right,
            clientY: rect.bottom,
            poiSetId,
            poiSetName,
            layers: setLayers
          });
        });

        setRow.appendChild(setToggle);
        setRow.appendChild(setCheckbox);
        setRow.appendChild(setLegendIcon);
        setRow.appendChild(setLabel);
        setRow.appendChild(setCount);
        setRow.appendChild(setActionsButton);
        ul.appendChild(setRow);

      }

      const title = (layer.get('title') || '').trim();
      if (!title) return; // skip unnamed
      const isSearchPoiCategory = group === window.own_map && layer.get('searchPoiCategory') === true;

      const li = document.createElement('li');
      li.className = 'layer-info-row';
      li.classList.toggle('layer-is-hidden', !layer.getVisible());
      li.style.listStyle = 'none';
      li.style.marginBottom = '4px';
      if (group === window.own_map && (poiSetId || isSearchPoiCategory)) li.classList.add('poi-category-row');
      if (poiSetId) {
        li.dataset.poiSetId = String(poiSetId);
        li.hidden = collapsedPoiSets.has(String(poiSetId));
      }
      if (isSearchPoiCategory) li.classList.add('search-poi-category-row');

const cb = document.createElement('input');

if (isOverlayGroup(group)) {
  //RADIO BUTTON
  cb.type = 'radio';
  cb.name = 'overlay-radio-group';
  cb.checked = !!layer.getVisible();

  cb.onchange = () => {
    if (!cb.checked) return;

    // Hide all other overlay layers
    group.getLayers().forEach(l => l.setVisible(false));

    // Show selected one
    layer.setVisible(true);

    // Sync all radios
    group.getLayers().forEach(l => {
      const otherCb = layerToCheckbox.get(l);
      if (otherCb) otherCb.checked = l === layer;
    });
  };

} else {
  // CHECKBOX (Base + Own Data)
  cb.type = 'checkbox';
  const poiFeatures = hasPoiLocations(layer) ? getPoiLocationFeatures(layer) : [];
  const visiblePoiCount = poiFeatures.filter(isPoiLocationVisible).length;
  cb.checked = !!layer.getVisible() && (poiFeatures.length === 0 || visiblePoiCount === poiFeatures.length);
  cb.indeterminate = !!layer.getVisible() && visiblePoiCount > 0 && visiblePoiCount < poiFeatures.length;

  cb.onchange = () => {
    const checked = cb.checked;
    if (hasPoiLocations(layer)) getPoiLocationFeatures(layer).forEach(feature => setPoiLocationVisible(feature, checked));
    layer.setVisible(checked);
    cb.indeterminate = false;
    syncLayerCheckbox(layer);
    updateGroupCheckbox(groupCb, group);
  };
}

      const disabledByRights = !!layer.get('disabledByRights');

      if (disabledByRights) {
        cb.disabled = true;
        cb.checked = false;

        li.style.opacity = '0.5';
        li.style.pointerEvents = 'none';
      }

      const label = document.createElement('span');
      label.className = 'layer-name';
      if (group === window.own_map && (poiSetId || isSearchPoiCategory)) label.classList.add('poi-category-name');
      const layerTitle = layer.get('title') || 'Unnamed Layer';
      label.textContent = ` ${layerTitle}`;
      if (group === window.own_map && (poiSetId || isSearchPoiCategory)) {
        label.textContent = '';
        const categoryLabelText = document.createElement('span');
        categoryLabelText.className = 'poi-category-label-text';
        categoryLabelText.textContent = layerTitle;
        const categoryCount = document.createElement('span');
        categoryCount.className = 'poi-node-count';
        categoryCount.textContent = String(getLayerFeatureCount(layer));
        label.append(categoryLabelText, categoryCount);
      }
      label.style.cursor = 'pointer';
      label.title = `${title} — click to zoom to layer`;
      label.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        zoomToLayerMinimum(layer);
      });

      const row = document.createElement('div');
      row.className = 'layer-info-row-main';
      if (group === window.own_map && (poiSetId || isSearchPoiCategory)) {
        const categoryKey = `${poiSetId || 'search'}:${title}`;
        const categoryToggle = document.createElement('button');
        categoryToggle.type = 'button';
        categoryToggle.className = 'poi-tree-toggle poi-category-toggle';
        categoryToggle.textContent = collapsedPoiCategories.has(categoryKey) ? '▶' : '▼';
        categoryToggle.setAttribute('aria-label', `Toggle ${title}`);
        categoryToggle.setAttribute('aria-expanded', String(!collapsedPoiCategories.has(categoryKey)));
        categoryToggle.addEventListener('click', event => {
          event.preventDefault(); event.stopPropagation();
          if (collapsedPoiCategories.has(categoryKey)) collapsedPoiCategories.delete(categoryKey); else collapsedPoiCategories.add(categoryKey);
          const collapsed = collapsedPoiCategories.has(categoryKey);
          categoryToggle.textContent = collapsed ? '▶' : '▼';
          categoryToggle.setAttribute('aria-expanded', String(!collapsed));
          li.querySelector('.poi-location-list')?.toggleAttribute('hidden', collapsed);
        });
        row.appendChild(categoryToggle);
        li.dataset.poiCategoryKey = categoryKey;
      }
      row.appendChild(cb);
      row.appendChild(label);

      const details = createQgisLayerControls(layer, populateLayerPanel);
      row.appendChild(details);

      li.appendChild(row);

      if (poiSetId || isSearchPoiCategory) {
        const markerList = document.createElement('ul');
        markerList.className = 'poi-location-list search-poi-marker-list';
        markerList.hidden = collapsedPoiCategories.has(li.dataset.poiCategoryKey);
        const locationFeatures = getPoiLocationFeatures(layer);
        const showLocationSearch = POI_CATEGORY_SEARCH_THRESHOLD === null
          || locationFeatures.length > POI_CATEGORY_SEARCH_THRESHOLD;
        if (showLocationSearch) {
          const categoryKey = li.dataset.poiCategoryKey;
          const searchRow = document.createElement('li');
          searchRow.className = 'poi-category-search-row';
          const searchInput = document.createElement('input');
          searchInput.type = 'search';
          searchInput.className = 'form-control form-control-sm';
          searchInput.placeholder = 'Find a location';
          searchInput.setAttribute('aria-label', `Search locations in ${title}`);
          searchInput.value = poiCategorySearchQueries.get(categoryKey) || '';
          searchInput.addEventListener('input', () => {
            poiCategorySearchQueries.set(categoryKey, searchInput.value);
            filterPoiCategoryLocations(markerList, categoryKey);
          });
          const status = document.createElement('div');
          status.className = 'poi-category-search-status';
          status.setAttribute('role', 'status');
          searchRow.append(searchInput, status);
          markerList.appendChild(searchRow);
        }
        locationFeatures.forEach(feature => {
          const markerItem = document.createElement('li');
          markerItem.className = 'poi-location-row search-poi-marker-row';
          const markerIcon = document.createElement('span');
          markerIcon.className = 'poi-location-symbol search-poi-marker-symbol';
          markerIcon.setAttribute('aria-hidden', 'true');
          const markerColor = layer.get?.('poiStyleColor') || '#e11d48';
          markerIcon.style.backgroundColor = markerColor;
          markerIcon.style.boxShadow = `0 0 0 1px ${markerColor}`;
          const markerLabel = document.createElement('button');
          markerLabel.type = 'button';
          markerLabel.className = 'poi-location-name search-poi-marker-name';
          markerLabel.textContent = feature.get('name') || feature.get('label') || feature.get('identifier') || 'Unnamed location';
          const markerCheckbox = document.createElement('input');
          markerCheckbox.type = 'checkbox';
          markerCheckbox.className = 'poi-location-checkbox';
          markerCheckbox.checked = isPoiLocationVisible(feature);
          featureToLocationCheckbox.set(feature, markerCheckbox);
          markerCheckbox.setAttribute('aria-label', `Show ${markerLabel.textContent}`);
          markerCheckbox.addEventListener('change', event => {
            event.stopPropagation();
            setPoiLocationVisible(feature, markerCheckbox.checked);
            const features = getPoiLocationFeatures(layer);
            if (markerCheckbox.checked && !layer.getVisible()) layer.setVisible(true);
            if (!features.some(isPoiLocationVisible)) layer.setVisible(false);
            syncLayerCheckbox(layer);
          });
          markerLabel.title = `${markerLabel.textContent} — click to zoom`;
          markerLabel.addEventListener('click', event => {
            event.preventDefault();
            event.stopPropagation();
            const geometry = feature.getGeometry?.();
            const currentMap = getMap();
            if (!geometry || !currentMap) return;
            currentMap.getView().fit(geometry.getExtent(), {
              padding: [60, 60, 60, 60],
              duration: 500,
              maxZoom: 17
            });
          });
          markerItem.append(markerCheckbox, markerIcon, markerLabel);
          markerList.appendChild(markerItem);
        });
        if (showLocationSearch) filterPoiCategoryLocations(markerList, li.dataset.poiCategoryKey);
        li.appendChild(markerList);
      }
      ul.appendChild(li);

      // remember mapping Layer -> checkbox for sync from outside
      layerToCheckbox.set(layer, cb);
      layerToLabel.set(layer, label);
      layerToRow.set(layer, li);
      updateLayerLabelState(layer);

      // attach a single visibility-change listener per layer
      if (!layer.__layerInfoListenerAdded) {
        layer.on('change:visible', () => syncLayerCheckbox(layer));
        try { layer.__layerInfoListenerAdded = true; } catch (e) { /* ignore */ }
      }
    });

    // initialize group checkbox state from the group's true visibility
    updateGroupCheckbox(groupCb, group);

    // Wire group-level checkbox
    if (groupCb && !isOverlayGroup(group)) {
      groupCb.onchange = () => {
        groupCb.dataset.userInteracted = 'true';
        const checked = groupCb.checked;
        const displayedLayers = getSortedLayers(group).filter(layer => !layer.get('disabledByRights'));
        displayedLayers.forEach(layer => {
          if (hasPoiLocations(layer)) {
            getPoiLocationFeatures(layer).forEach(feature => setPoiLocationVisible(feature, checked));
          }
          layer.setVisible(checked);
          syncLayerCheckbox(layer);
        });
        updateGroupCheckbox(groupCb, group);
      };
    }
  });
}

// small helper to toggle arrow text
function setArrow(button, open) {
  if (!button) return;
  button.textContent = (open ? '▼ ' : '▶ ') + button.textContent.trim().slice(1).trim();
}

window.addEventListener('DOMContentLoaded', () => {

  const layerInfoBtn = document.getElementById('layer-info-btn');
  const layerInfoPanel = document.getElementById('layer-info-panel');
  let layerInfoMinimized = false;
  let layerInfoStoredGeometry = null;

  if (!layerInfoBtn || !layerInfoPanel) {
    console.error("Layer button or panel not found");
    return;
  }

  if (layerInfoPanel.parentElement !== document.body) {
    document.body.appendChild(layerInfoPanel);
  }

  const header = document.getElementById('layer-info-header');
  const panelTitle = header?.querySelector('h6');
  const closeBtn = header?.querySelector('.btn-close');

  if (header && closeBtn && !document.getElementById('layer-info-opacity')) {
    const opacityControl = document.createElement('label');
    opacityControl.className = 'summary-opacity-control';
    opacityControl.title = 'Transparency';
    opacityControl.setAttribute('data-no-drag', 'true');
    opacityControl.innerHTML = `
      <i class="bi bi-circle-half" aria-hidden="true"></i>
      <span class="summary-opacity-slider">
        <input id="layer-info-opacity" type="range" min="30" max="100" step="5" value="100" aria-label="Layer Info transparency">
        <output for="layer-info-opacity" style="left:47px">100%</output>
      </span>`;
    header.insertBefore(opacityControl, closeBtn);
  }

  if (header && panelTitle && closeBtn && !document.getElementById('layer-info-minimize-btn')) {
    const minimizeBtn = document.createElement('button');
    minimizeBtn.type = 'button';
    minimizeBtn.id = 'layer-info-minimize-btn';
    minimizeBtn.className = 'btn btn-sm btn-outline-secondary sevenn-panel-toggle layer-info-minimize-btn';
    minimizeBtn.innerHTML = '<i class="bi bi-dash-lg"></i>';
    minimizeBtn.title = 'Dock to left';
    minimizeBtn.setAttribute('aria-label', 'Dock layer info panel to left');
    minimizeBtn.setAttribute('data-no-drag', 'true');
    header.insertBefore(minimizeBtn, closeBtn);
  }

  const minimizeBtn = document.getElementById('layer-info-minimize-btn');
  const layersTab = document.getElementById('layer-info-layers-tab');
  const legendTab = document.getElementById('layer-info-legend-tab');
  const baseGroupLabel = document.querySelector('.group-toggle[data-target="base-layers"] span');
  const opacitySlider = document.getElementById('layer-info-opacity');
  let layerInfoOpacityLabelTimer = null;
  let overlayHiddenBeforeLegend = null;
  layerInfoPanel.style.opacity = '1';

  const setBaseGroupLabel = showLegend => {
    const toggle = document.querySelector('.group-toggle[data-target="base-layers"]');
    if (!toggle) return;
    let label = toggle.querySelector('span');
    if (!label) {
      const arrow = toggle.textContent.trim().startsWith('▶') ? '▶ ' : '▼ ';
      toggle.textContent = arrow;
      label = document.createElement('span');
      label.dataset.translate = 'layer_groups.base_data';
      toggle.appendChild(label);
    }
    label.textContent = showLegend ? 'Bases & References' : 'Base Data';
  };

  const selectLayerInfoTab = tabName => {
    const showLegend = tabName === 'legend';
    setBaseGroupLabel(showLegend);
    if (layerInfoPanel.classList.contains('legend-tab-active') === showLegend) return;
    layerInfoPanel.classList.toggle('legend-tab-active', showLegend);
    layersTab?.classList.toggle('active', !showLegend);
    legendTab?.classList.toggle('active', showLegend);
    layersTab?.setAttribute('aria-selected', String(!showLegend));
    legendTab?.setAttribute('aria-selected', String(showLegend));
    const baseLayers = document.getElementById('base-layers');
    const overlayLayers = document.getElementById('overlay-layers');
    if (showLegend && baseLayers && overlayLayers) {
      overlayHiddenBeforeLegend = overlayLayers.classList.contains('d-none');
      overlayLayers.classList.toggle('d-none', baseLayers.classList.contains('d-none'));
      layerInfoPanel.classList.toggle('bases-references-collapsed', baseLayers.classList.contains('d-none'));
    } else if (!showLegend && overlayLayers && overlayHiddenBeforeLegend !== null) {
      overlayLayers.classList.toggle('d-none', overlayHiddenBeforeLegend);
      overlayHiddenBeforeLegend = null;
      layerInfoPanel.classList.remove('bases-references-collapsed');
    }
  };
  layersTab?.addEventListener('click', () => selectLayerInfoTab('layers'));
  legendTab?.addEventListener('click', () => selectLayerInfoTab('legend'));

  opacitySlider?.addEventListener('input', event => {
    const value = Number(event.currentTarget.value);
    layerInfoPanel.style.opacity = String(value / 100);
    const output = header?.querySelector('output[for="layer-info-opacity"]');
    if (!output) return;

    output.textContent = `${value}%`;
    output.style.left = `${7 + ((value - 30) / 70) * 40}px`;
    output.classList.add('is-visible');
    window.clearTimeout(layerInfoOpacityLabelTimer);
    layerInfoOpacityLabelTimer = window.setTimeout(
      () => output.classList.remove('is-visible'),
      2000
    );
  });

  let dockButton = document.getElementById('layer-info-panel-dock');

  if (!dockButton) {
    dockButton = document.createElement('button');
    dockButton.type = 'button';
    dockButton.id = 'layer-info-panel-dock';
    dockButton.className = 'layer-info-panel-dock btn btn-light border border-start-0 shadow-sm';
    dockButton.innerHTML = '<i class="bi bi-chevron-left"></i>';
    dockButton.title = 'Restore layer info';
    dockButton.setAttribute('aria-label', 'Restore layer info panel');
    dockButton.style.display = 'none';
    document.body.appendChild(dockButton);
  }
  registerDock('layer-info', dockButton, 72, '#layer-info-btn');

  const captureLayerInfoGeometry = () => ({
    left: layerInfoPanel.style.left || '',
    right: layerInfoPanel.style.right || '',
    top: layerInfoPanel.style.top || '',
    bottom: layerInfoPanel.style.bottom || '',
    width: layerInfoPanel.style.width || '',
    height: layerInfoPanel.style.height || '',
  });

  const applyLayerInfoGeometry = (geometry) => {
    if (!geometry) return;
    if (geometry.left) layerInfoPanel.style.setProperty('left', geometry.left, 'important');
    else layerInfoPanel.style.removeProperty('left');
    if (geometry.right) layerInfoPanel.style.setProperty('right', geometry.right, 'important');
    else layerInfoPanel.style.removeProperty('right');
    if (geometry.top) layerInfoPanel.style.setProperty('top', geometry.top, 'important');
    else layerInfoPanel.style.removeProperty('top');
    if (geometry.bottom) layerInfoPanel.style.setProperty('bottom', geometry.bottom, 'important');
    else layerInfoPanel.style.removeProperty('bottom');
    if (geometry.width) layerInfoPanel.style.setProperty('width', geometry.width, 'important');
    else layerInfoPanel.style.removeProperty('width');
    if (geometry.height) layerInfoPanel.style.setProperty('height', geometry.height, 'important');
    else layerInfoPanel.style.removeProperty('height');
  };

  const applyLayerInfoPanelState = () => {
    layerInfoPanel.classList.toggle('is-minimized', layerInfoMinimized);
    layerInfoPanel.style.display = layerInfoMinimized ? 'none' : '';
    layerInfoPanel.style.visibility = '';
    if (!layerInfoMinimized && layerInfoStoredGeometry) {
      applyLayerInfoGeometry(layerInfoStoredGeometry);
    }
    setDockVisible('layer-info', layerInfoMinimized);
  };

  const setLayerInfoMinimized = (minimized) => {
    if (minimized) {
      layerInfoStoredGeometry = captureLayerInfoGeometry();
    }
    layerInfoMinimized = minimized;
    applyLayerInfoPanelState();
    if (!layerInfoMinimized) populateLayerPanel();
  };

  layerInfoPanel.__sevennSetVisible = (visible) => {
    if (visible) {
      if (layerInfoStoredGeometry) {
        applyLayerInfoGeometry(layerInfoStoredGeometry);
      }
      setLayerInfoMinimized(false);
      layerInfoPanel.classList.remove('d-none');
      requestAnimationFrame(() => placeFloatingDialog(layerInfoPanel));
    } else {
      layerInfoMinimized = false;
      layerInfoPanel.classList.add('d-none');
      setDockVisible('layer-info', false);
    }
  };
  layerInfoPanel.__sevennSetMinimized = setLayerInfoMinimized;
  layerInfoPanel.__sevennGetMinimized = () => layerInfoMinimized;

  if (minimizeBtn) {
    minimizeBtn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      layerInfoPanel.classList.remove('d-none');
      setLayerInfoMinimized(true);
    });
  }

  dockButton.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    layerInfoPanel.classList.remove('d-none');
    setLayerInfoMinimized(false);
    requestAnimationFrame(() => placeFloatingDialog(layerInfoPanel));
  });

  // =========================================
  // TREE GROUP COLLAPSE / EXPAND
  // =========================================

  const groups = [
    {
      headerId: 'base-data-header',
      contentId: 'base-layers',
      defaultOpen: true
    },
    {
      headerId: 'overlay-header',
      contentId: 'overlay-layers',
      defaultOpen: false
    },
    {
      headerId: 'own-data-header',
      contentId: 'own-layers',
      defaultOpen: false
    }
  ];

  groups.forEach(g => {

    const header = document.getElementById(g.headerId);
    const content = document.getElementById(g.contentId);

    if (!header || !content) {
      if (content && document.querySelector(`.group-toggle[data-target="${g.contentId}"]`)) {
        return;
      }
      console.warn("Missing tree group:", g.headerId, g.contentId);
      return;
    }

    // Initial state
    content.style.display = g.defaultOpen ? 'block' : 'none';

    let arrow = header.querySelector('.tree-arrow');

    if (!arrow) {
      arrow = document.createElement('span');
      arrow.className = 'tree-arrow';
      arrow.style.marginRight = '6px';
      header.prepend(arrow);
    }

    arrow.textContent = g.defaultOpen ? '▼' : '▶';

    header.style.cursor = 'pointer';

    header.addEventListener('click', () => {

      const isOpen = content.style.display !== 'none';

      content.style.display = isOpen ? 'none' : 'block';

      arrow.textContent = isOpen ? '▶' : '▼';
    });
  });

  // =========================================
  // GROUP COLLAPSE / EXPAND
  // =========================================
  document.querySelectorAll('.group-toggle').forEach(btn => {

    btn.addEventListener('click', () => {

      const targetId = btn.dataset.target;
      const target = document.getElementById(targetId);

      if (!target) return;

      // toggle visibility
      target.classList.toggle('d-none');

      // update arrow
      const isHidden = target.classList.contains('d-none');

      if (targetId === 'base-layers' && layerInfoPanel.classList.contains('legend-tab-active')) {
        document.getElementById('overlay-layers')?.classList.toggle('d-none', isHidden);
        layerInfoPanel.classList.toggle('bases-references-collapsed', isHidden);
      }

      if (isHidden && targetId === 'own-layers' && layerInfoPanel.classList.contains('legend-tab-active')) {
        collapseAllPoiTreeRoots(target);
      }

      let text = btn.textContent.trim();

      // remove existing arrow
      text = text.replace(/^▼\s*/, '').replace(/^▶\s*/, '');

      btn.textContent = (isHidden ? '▶ ' : '▼ ') + text;
    });

  });

  // =========================================
  // LAYER INFO PANEL SHOW/HIDE
  // =========================================

  layerInfoBtn.addEventListener('click', () => {

    const isHidden = layerInfoPanel.classList.contains('d-none');

    if (isHidden) {

      layerInfoPanel.classList.remove('d-none');
      setLayerInfoMinimized(false);
      requestAnimationFrame(() => placeFloatingDialog(layerInfoPanel));

    } else {
      if (layerInfoMinimized) {
        setLayerInfoMinimized(false);
      } else {
        layerInfoPanel.classList.add('d-none');
        setDockVisible('layer-info', false);
      }
    }
  });

  applyLayerInfoPanelState();
});

function addLayerToGroup(layer, groupId) {
  const list = document.getElementById(groupId);
  const li = document.createElement('li');

  const cb = document.createElement('input');
  cb.type = 'checkbox';
  cb.checked = layer.getVisible();
  cb.onchange = () => layer.setVisible(cb.checked);

  li.appendChild(cb);
  li.appendChild(document.createTextNode(layer.get('title')));
  list.appendChild(li);
}

document.addEventListener('base-layers-updated', () => {

  populateLayerPanel();
});

document.addEventListener('poi-layers-updated', () => {
  populateLayerPanel();
});

document.addEventListener('base-layer-zoom-visibility-updated', () => {
  refreshLayerLabelStates();
});

let selectedAreasRefreshFrame = null;
document.addEventListener('selected-areas-updated', () => {
  window.cancelAnimationFrame(selectedAreasRefreshFrame);
  selectedAreasRefreshFrame = window.requestAnimationFrame(populateSelectedAreas);
});

